/** @flow */

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import http from "@http";
import type { Action } from "redux-action";
import type { PaginationType } from "@types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import config from "@config";
import managementActions from "@modules/management/actionCreator";
import {
  GetManagementCallbackQueues,
  GetActiveManagementCallbacks,
  GetCompletedManagementCallbacks,
  UpdateManagementCallback,
  CallbacksEmployeeSearch,
  CallbacksEmployeeExtendedSearch,
  CreateCallback,
} from "@modules/management/constants/actions";
import { getEmployeeExtendedFilters } from "@modules/employee/selectors";
import { formatDateToInputFormat } from "@transformers/date";
import { transformFilters, OPERATORS } from "@transformers/request";
import type { CallbackQueues, ActiveCallbackItem } from "@modules/management/types";
import {
  selectActiveCallbacksPage,
  selectActiveCallbacksFilters,
  selectEmployeesPage,
  selectCompletedCallbacksPage,
  selectCompletedCallbacksFilters,
} from "@modules/management/selectors";
import { sortToString } from "@transformers/sort";

type UpdateCallbackPayload = $Exact<{
  id: number,
  data: Object,
  cb?: Function,
  successMessage?: string,
}>;

type FilesRequestPayload = $Exact<{
  page?: number,
}>;

type CompletedManagementCallbacksRequest = $Exact<{
  ...FilesRequestPayload,
  queryString?: string,
}>;

type FilesResponse = $Exact<{
  content: Array<ActiveCallbackItem>,
  pagination: PaginationType,
}>;

export function* onGetCallbackQueues(): Generator<*, void, { data: CallbackQueues }> {
  try {
    const response = yield call(http.get, `/api/v1/contact-centre/callback-queue`);

    yield put(managementActions.GetManagementCallbackQueuesSuccess(response.data));
  } catch (e) {
    yield put(managementActions.GetManagementCallbackQueuesError(e.response));
  }
}

export function* onGetActiveManagementCallbacks({
  payload,
}: Action<FilesRequestPayload>): Generator<void, void, { data: FilesResponse }> {
  const currentPage = yield select(selectActiveCallbacksPage);
  const search = yield select(selectActiveCallbacksFilters);
  try {
    const page = (payload && payload.page) || currentPage;

    const response = yield call(http.get, `/api/v1/contact-centre/callback`, {
      params: {
        search: transformFilters({ ...search, status: "OPEN" }),
        page,
        limit: config.resultsPerPage,
        sort: sortToString([
          {
            property: "dateDue",
            direction: "asc",
          },
        ]),
      },
    });

    yield put(managementActions.GetActiveManagementCallbacksSuccess(response.data));
  } catch (e) {
    yield put(managementActions.GetActiveManagementCallbacksError(e.response));
  }
}

export function* onGetCompletedManagementCallbacks({
  payload,
}: Action<CompletedManagementCallbacksRequest>): Generator<void, void, *> {
  const currentPage = yield select(selectCompletedCallbacksPage);
  const _filters = yield select(selectCompletedCallbacksFilters);
  const filters = { ..._filters };

  const fromDate = filters.fromDate || null;
  const toDate = filters.toDate || null;
  const clientId = filters.clientId || null;
  const completionStatus = filters.completionStatus || null;

  let queries = [];

  for (let i in filters) {
    if (filters[i] === null) delete filters[i];
  }
  if (typeof clientId === "string") {
    delete filters.clientId;
  }

  if (fromDate) {
    delete filters.fromDate;
    queries.push(
      transformFilters(
        { updatedAt: formatDateToInputFormat(fromDate) },
        { updated_at: OPERATORS.GREATER_THAN },
      ),
    );
  }

  if (toDate) {
    delete filters.toDate;
    queries.push(
      transformFilters(
        { updatedAt: formatDateToInputFormat(toDate) },
        { updated_at: OPERATORS.LESS_THAN },
      ),
    );
  }

  const search = [
    ...queries,
    ...(!completionStatus
      ? ["SUCCESS", "REMOVED", "FAILED"].map(status => transformFilters({ status }))
      : []),
    ...(Object.keys(filters).length ? [transformFilters(filters)] : []),
  ].join();

  try {
    const page = (payload && payload.page) || (currentPage > 0 ? currentPage : 1);
    const response = yield call(http.get, `/api/v1/contact-centre/callback/archive`, {
      params: {
        search,
        page,
        limit: config.resultsPerPage,
        sort: sortToString([
          {
            property: "updatedAt",
            direction: "desc",
          },
        ]),
      },
    });

    yield put(managementActions.GetCompletedManagementCallbacksSuccess(response.data));
  } catch (e) {
    yield put(managementActions.GetCompletedManagementCallbacksError(e.response));
  }
}

export function* onUpdateManagementCallback({
  payload,
}: Action<UpdateCallbackPayload>): Generator<void, void, Object> {
  if (!payload || !payload.data || !payload.id) {
    throw Error("Invalid payload provided");
  }
  try {
    yield call(http.put, `/api/v1/contact-centre/callback/${payload.id}`, payload.data);
    yield all([
      put(managementActions.UpdateManagementCallbackSuccess()),
      put(managementActions.GetActiveManagementCallbacks()),
      put(managementActions.GetCompletedManagementCallbacks()),
      put(managementActions.GetManagementCallbackQueues()),
    ]);

    if (payload.cb) {
      yield put(payload.cb());
    }
    if (payload.successMessage) {
      yield put(sendSuccessMessage(payload.successMessage));
    }
  } catch (e) {
    yield put(managementActions.UpdateManagementCallbackError(e.response));
  }
}

export function* onCallbacksEmployeeSearch({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  if (!payload || !payload.data || !payload.clientId) {
    throw Error("Invalid payload provided");
  }
  const currentPage = yield select(selectEmployeesPage);
  try {
    const page = (payload && payload.page) || currentPage;

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee`,
      {
        params: {
          search: transformFilters(payload.data, { last_name: OPERATORS.STARTS_WITH }),
          page,
          limit: payload.limit || config.shortResultsPerPage,
        },
      },
    );

    yield put(managementActions.CallbacksEmployeeSearchSuccess(response.data));
  } catch (e) {
    yield put(managementActions.CallbacksEmployeeSearchError(e.response));
  }
}

export function* onCallbacksEmployeeExtendedSearch({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }
  const currentPage = yield select(selectEmployeesPage);
  const filters = yield select(getEmployeeExtendedFilters);

  try {
    const page = (payload || {}).page || currentPage;

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee`,
      {
        params: {
          search: transformFilters(Object.assign({}, filters.client, filters), {
            last_name: OPERATORS.LIKE,
          }),
          page,
          limit: config.shortResultsPerPage,
        },
      },
    );

    yield put(managementActions.CallbacksEmployeeExtendedSearchSuccess(response.data));
  } catch (e) {
    yield put(managementActions.CallbacksEmployeeSearchError(e.response));
  }
}

export function* onCreateCallback({ payload }: Action<Object>): Generator<void, void, Object> {
  if (!payload) {
    throw Error("Invalid payload provided");
  }
  try {
    yield call(http.post, `/api/v1/contact-centre/callback`, payload);
    yield all([
      put(managementActions.CreateCallbackSuccess()),
      put(sendSuccessMessage("CALLBACK_ADDED")),
      put(managementActions.GetActiveManagementCallbacks()),
      put(managementActions.GetManagementCallbackQueues()),
      put(managementActions.ToggleCreateCallbackModal(false)),
    ]);
  } catch (e) {
    yield put(managementActions.CreateCallbackError(e.response));
  }
}

export function* CallbackAdminSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetManagementCallbackQueues, onGetCallbackQueues),
    takeEvery(GetActiveManagementCallbacks, onGetActiveManagementCallbacks),
    takeEvery(GetCompletedManagementCallbacks, onGetCompletedManagementCallbacks),
    takeEvery(UpdateManagementCallback, onUpdateManagementCallback),
    takeEvery(CallbacksEmployeeSearch, onCallbacksEmployeeSearch),
    takeEvery(CallbacksEmployeeExtendedSearch, onCallbacksEmployeeExtendedSearch),
    takeEvery(CreateCallback, onCreateCallback),
  ]);
}

export default CallbackAdminSagas;
